import {FolderUsageHintComponent} from '../../../folders/folder-usage-hint/folder-usage-hint.component';
import {Component, Inject, OnInit, Optional} from '@angular/core';
import {FormBuilder, FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormGroup, Validators} from '@angular/forms';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {FormOptionsLists} from '../../../../shared/FormOptionsLists';
import {MatOptionModule} from '@angular/material/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {VerbalActionEntryService} from '../../../../services/verbal-action/verbal-action-entry.service';
import {VerbalActionEntryCreationDto} from '../../../../dtos/verbal-action/entry/verbal-action-entry-creation-dto';
import {VerbalActionEntryDto} from '../../../../dtos/verbal-action/entry/verbal-action-entry-dto';
import {NotificationService} from '../../../../services/common/notification.service';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {CommonModule} from '@angular/common';
import {SelectDialogComponent} from '../../../select/select-dialog/select-dialog.component';

export interface NewVerbalActionDialogData {
  id: number;
  service: VerbalActionEntryService;
}

@Component({
  selector: 'app-new-verbal-action',
  imports: [
    CommonModule,
    FormsModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatDialogModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    FolderUsageHintComponent,
  ],
  templateUrl: './new-verbal-action.component.html',
  standalone: true,
  styleUrl: './new-verbal-action.component.scss'
})
export class NewVerbalActionComponent implements OnInit {

  formOptionsLists = new FormOptionsLists();
  levelList = this.formOptionsLists.levelList.filter(o => o !== 'NONE');

  verbalActionFormGroup!: UntypedFormGroup;
  selectedCategory: {id: number; name: string} | null = null;
  edit = false;
  id: number | null = null;
  existingEntry: VerbalActionEntryDto | null = null;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<NewVerbalActionComponent>,
    private verbalActionEntryService: VerbalActionEntryService,
    private notificationService: NotificationService,
    private dialog: MatDialog,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: NewVerbalActionDialogData
  ) {}

  ngOnInit(): void {
    this.verbalActionFormGroup = this.fb.group({
      nameCtrl: ['', Validators.required],
      receptive: ['NONE'],
      productive: ['NONE'],
      categoryId: [{value: null, disabled: true}, Validators.required],
      examples: this.fb.array([]),
    });

    if (this.data?.id) {
      this.edit = true;
      this.id = this.data.id;
      this.verbalActionEntryService.getEntry(this.id).subscribe({
        next: (entry) => {
          this.existingEntry = entry;
          this.setForm(entry);
        },
        error: (error) => this.notificationService.openSnackBar(error)
      });
    } else {
      this.addExample();
    }
  }

  setForm(entry: VerbalActionEntryDto): void {
    if (entry.category) {
      this.selectedCategory = {id: entry.category.id, name: entry.category.name};
      this.verbalActionFormGroup.controls['categoryId'].setValue(entry.category.id);
    }
    this.verbalActionFormGroup.patchValue({
      nameCtrl: entry.name,
      receptive: entry.receptive ?? 'NONE',
      productive: entry.productive ?? 'NONE',
    });
    this.examples().clear();
    (entry.examples ?? []).forEach(ex => {
      this.examples().push(this.fb.group({example: ex}));
    });
    if (this.examples().length === 0) {
      this.addExample();
    }
  }

  openCategorySelectionDialog(): void {
    const dialogRef = this.dialog.open(SelectDialogComponent, {
      data: {
        selectedObject: this.selectedCategory,
        displayedColumns: ['single-select', 'voc-cat-name', 'voc-cat-parent'],
        selectionEntity: 'va_category',
        dialogTitle: 'Thema auswählen',
        selectionMode: 'single-select',
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) {
        if (result && result.length > 0) {
          this.selectedCategory = result[0];
          this.verbalActionFormGroup.controls['categoryId'].setValue(result[0].id);
        } else {
          this.selectedCategory = null;
          this.verbalActionFormGroup.controls['categoryId'].setValue(null);
        }
      }
    });
  }

  examples(): UntypedFormArray {
    return this.verbalActionFormGroup.get('examples') as UntypedFormArray;
  }

  addExample(): void {
    this.examples().push(this.fb.group({example: ''}));
  }

  removeExample(i: number): void {
    this.examples().removeAt(i);
  }

  save(): void {
    if (this.verbalActionFormGroup.controls['nameCtrl'].invalid || !this.selectedCategory) return;
    const dto = new VerbalActionEntryCreationDto(
      this.id,
      this.verbalActionFormGroup.value.nameCtrl,
      this.verbalActionFormGroup.value.receptive || 'NONE',
      this.verbalActionFormGroup.value.productive || 'NONE',
      this.examples().controls.map(c => c.value.example as string).filter(e => e?.trim() !== ''),
      this.selectedCategory.id,
      this.existingEntry ? this.getIds(this.existingEntry.vocabCategories) : [],
      this.existingEntry ? this.getIds(this.existingEntry.vocabEntries) : [],
      this.existingEntry ? this.getIds(this.existingEntry.functionalGrammarCategories) : [],
      this.existingEntry ? this.getIds(this.existingEntry.functionalGrammarEntries) : [],
      this.existingEntry ? this.getIds(this.existingEntry.systemicGrammarCategories) : [],
      this.existingEntry ? this.getIds(this.existingEntry.systemicGrammarEntries) : [],
      this.existingEntry ? this.getIds(this.existingEntry.textPatterns) : [],
      this.existingEntry ? this.getIds(this.existingEntry.textTypes) : [],
      this.existingEntry ? this.getIds(this.existingEntry.detailedCanDoStatementCategories) : [],
      this.existingEntry ? this.getIds(this.existingEntry.detailedCanDoStatementEntries) : [],
      this.existingEntry ? this.getIds(this.existingEntry.globalCanDoStatementCategories) : [],
      this.existingEntry ? this.getIds(this.existingEntry.globalCanDoStatementEntries) : [],
      this.existingEntry ? this.getIds((this.existingEntry as any).verbalActionCategories) : [],
      this.existingEntry ? this.getIds((this.existingEntry as any).verbalActionEntries) : [],
    );

    const request = this.edit
      ? this.verbalActionEntryService.updateEntry(this.id, dto)
      : this.verbalActionEntryService.createEntry(dto);

    request.subscribe({
      next: (data) => {
        this.notificationService.openSnackBar(
          'Sprachhandlung "' + data.name + '" erfolgreich ' + (this.edit ? 'aktualisiert' : 'erstellt')
        );
        this.dialogRef.close(data);
      },
      error: (error) => this.notificationService.openSnackBar(error)
    });
  }

  private getIds(items: any[]): number[] {
    return (items ?? []).map(i => i.id).filter(id => id != null);
  }
}
